import React from 'react';
import { X } from 'lucide-react';

interface DetailRow {
  label: string;
  value: string;
}

interface ConfirmDetailsModalProps {
  isOpen: boolean;
  title?: string;
  details: DetailRow[];
  description?: React.ReactNode;
  confirmLabel?: string;
  editLabel?: string;
  onConfirm: () => void;
  onEdit: () => void;
  onClose: () => void;
}

const ConfirmDetailsModal: React.FC<ConfirmDetailsModalProps> = ({
  isOpen,
  title = 'Quick Check',
  details,
  description,
  confirmLabel = 'Confirm',
  editLabel = 'Edit',
  onConfirm,
  onEdit,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 flex items-center justify-center z-[1001] p-4 zoomOutLevel">
      <div className="bg-[var(--modal-background)] rounded-2xl shadow-xl max-w-md w-full mx-4 animate-scale-in">
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--color-sub-title-text)] hover:text-[var(--color-text)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 pb-4">
          <div className="rounded-lg border border-[var(--button-border-color)] bg-[#E9EBF1] dark:bg-[#1E222A] p-3 space-y-1">
            {details.map((row) => (
              <p
                key={row.label}
                className="text-sm text-[var(--color-text)] break-words"
              >
                <span className="font-medium">{row.label}:</span>{' '}
                <span className="text-[var(--color-sub-title-text)]">
                  {row.value}
                </span>
              </p>
            ))}
          </div>

          {description && (
            <p className="mt-4 text-sm text-[var(--color-sub-title-text)]">
              {description}
            </p>
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onEdit}
            className="flex-1 px-4 py-3.5 text-sm font-bold text-[var(--color-terms-link)] border border-[var(--gradient-page-bottom-color)] rounded-lg hover:bg-[var(--button-hover)] transition-colors"
          >
            {editLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 py-3.5 px-4 text-sm font-bold text-white bg-gradient-to-r from-[var(--gradient-button-left-color)] to-[var(--gradient-button-right-color)] rounded-lg hover:shadow-lg transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDetailsModal;
